import React from "react";
import {
  Box,
  Typography,
  Paper,
  Icon,
  Chip,
  alpha,
  Skeleton,
  Pagination,
} from "@mui/material";
import { POActivityLog } from "../../types/po.types";

interface POActivityTimelineProps {
  activities: POActivityLog[];
  loading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  showPagination?: boolean;
}

// Activity configuration for PO-specific icons, colors, and labels
const poActivityConfig: Record<
  string,
  {
    icon: string;
    color: string;
    bgColor: string;
    label: string;
    description: string;
  }
> = {
  created: {
    icon: "add_circle",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    label: "Created",
    description: "Purchase Order was created from RFQ",
  },
  updated: {
    icon: "edit",
    color: "#0891B2",
    bgColor: "#ECFEFF",
    label: "Updated",
    description: "Purchase Order details were modified",
  },
  submitted: {
    icon: "send",
    color: "#7C3AED",
    bgColor: "#F3E8FF",
    label: "Submitted",
    description: "Purchase Order submitted for approval",
  },
  approved: {
    icon: "check_circle",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Approved",
    description: "Purchase Order approved by manager",
  },
  rejected: {
    icon: "cancel",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    label: "Rejected",
    description: "Purchase Order rejected",
  },
  ordered: {
    icon: "local_shipping",
    color: "#7C3AED",
    bgColor: "#F3E8FF",
    label: "Ordered",
    description: "Items ordered from supplier",
  },
  received: {
    icon: "inventory",
    color: "#0891B2",
    bgColor: "#ECFEFF",
    label: "Received",
    description: "Items received and added to inventory",
  },
  closed: {
    icon: "task_alt",
    color: "#6B7280",
    bgColor: "#F9FAFB",
    label: "Closed",
    description: "Purchase Order completed and closed",
  },
  agreement_generated: {
    icon: "description",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    label: "Agreement Generated",
    description: "Order agreement document generated",
  },
};

const formatTimestamp = (timestamp: number) => {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
};

const POActivityTimelineItem: React.FC<{
  activity: POActivityLog;
  isLast: boolean;
}> = ({ activity, isLast }) => {
  const config = poActivityConfig[activity.action] || poActivityConfig.updated;

  return (
    <Box sx={{ position: "relative", pb: isLast ? 0 : 3 }}>
      {/* Timeline line */}
      {!isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 20,
            top: 48,
            bottom: 0,
            width: 2,
            bgcolor: "#E5E7EB",
            zIndex: 0,
          }}
        />
      )}

      <Box sx={{ display: "flex", gap: 2, position: "relative", zIndex: 1 }}>
        {/* Timeline icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: config.bgColor,
            color: config.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${config.color}`,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }}>{config.icon}</Icon>
        </Box>

        {/* Activity content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: `1px solid ${alpha(config.color, 0.2)}`,
              "&:hover": {
                boxShadow: 1,
                borderColor: alpha(config.color, 0.3),
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    bgcolor: config.bgColor,
                    color: config.color,
                    fontWeight: 600,
                    border: `1px solid ${alpha(config.color, 0.3)}`,
                  }}
                />
                {activity.old_values && activity.new_values && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Status Changed
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {formatTimestamp(activity.timestamp)}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                mb: activity.notes || activity.details ? 1 : 0,
                lineHeight: 1.5,
              }}
            >
              {activity.details || config.description}
            </Typography>

            {/* Status change information */}
            {activity.previous_status && activity.new_status && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    lineHeight: 1.4,
                  }}
                >
                  Status changed from{" "}
                  <strong>{activity.previous_status}</strong> to{" "}
                  <strong>{activity.new_status}</strong>
                </Typography>
              </Box>
            )}

            {/* Additional details */}
            {activity.notes && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                  lineHeight: 1.4,
                }}
              >
                {activity.notes}
              </Typography>
            )}

            {/* User information */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1.5,
                pt: 1.5,
                borderTop: `1px solid ${alpha("#000", 0.08)}`,
              }}
            >
              <Icon sx={{ fontSize: 16, color: "text.secondary" }}>person</Icon>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="caption" color="text.secondary">
                  {activity.user_name ||
                    activity.changed_by_email?.split("@")[0] ||
                    "Unknown User"}
                </Typography>
                {(activity.user_email || activity.changed_by_email) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.65rem", opacity: 0.8 }}
                  >
                    {activity.user_email || activity.changed_by_email}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const POActivityTimelineSkeleton: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
    {[1, 2, 3].map((item) => (
      <Box key={item} sx={{ display: "flex", gap: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="rectangular"
            height={100}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Box>
    ))}
  </Box>
);

export const POActivityTimeline: React.FC<POActivityTimelineProps> = ({
  activities,
  loading = false,
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  showPagination = true,
}) => {
  if (loading) {
    return <POActivityTimelineSkeleton />;
  }

  if (activities.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          textAlign: "center",
        }}
      >
        <Icon
          sx={{
            fontSize: 48,
            color: "text.secondary",
            mb: 2,
          }}
        >
          history
        </Icon>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Activity Yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 300 }}
        >
          Purchase Order activity will appear here as actions are performed on
          this order.
        </Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box>
      {/* Activity Timeline */}
      <Box sx={{ mb: showPagination && totalPages > 1 ? 3 : 0 }}>
        {activities.map((activity, index) => (
          <POActivityTimelineItem
            key={`${activity.timestamp}-${activity.action}-${index}`}
            activity={activity}
            isLast={index === activities.length - 1}
          />
        ))}
      </Box>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange?.(newPage)}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};
